// Browser-side MediaStream → PCM 16-kHz mono Int16 async iterator.
//
// Transcribe's streaming API requires raw PCM, little-endian, signed 16-bit,
// at 16 kHz mono. Browsers hand us a MediaStream at the audio hardware's
// native rate (typically 44.1 or 48 kHz) in Float32. We downsample + convert
// in an AudioWorklet because:
//
//   - A ScriptProcessorNode works but runs on the main thread and clicks
//     under load — not acceptable for a confidentiality-signal UI that must
//     feel responsive.
//   - MediaRecorder gives us encoded formats (webm/opus) that Transcribe
//     doesn't accept for streaming.
//
// The worklet runs in the audio thread, emits Int16 chunks via its port, and
// this file wraps the port into an async iterator the Transcribe SDK can
// consume (`AudioStream`). Pause/stop fully release the MediaStreamTrack so
// the OS mic indicator disappears — per designer: "pausing must actually
// stop audio capture, not just stop sending it."

// Inline worklet source. We register it from a Blob URL so there's no extra
// static asset to serve. The ratio is computed at construction time from the
// AudioContext's actual sample rate.
const WORKLET_SOURCE = `
class PcmDownsampler extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const srcRate = sampleRate; // AudioWorkletGlobalScope-provided
    const dstRate = 16000;
    this.ratio = srcRate / dstRate;
    this.carry = [];
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;
    const channel = input[0]; // mono
    if (!channel || channel.length === 0) return true;

    // Append carry (fractional sample position from last callback) then
    // walk the input at this.ratio spacing. Linear interpolation keeps the
    // artefacts modest at speech frequencies.
    const buf = this.carry.length
      ? Float32Array.from([...this.carry, ...channel])
      : channel;
    const outLen = Math.floor(buf.length / this.ratio);
    const out = new Int16Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const pos = i * this.ratio;
      const lo = Math.floor(pos);
      const hi = Math.min(lo + 1, buf.length - 1);
      const frac = pos - lo;
      const s = buf[lo] * (1 - frac) + buf[hi] * frac;
      // Clamp + convert to Int16.
      const clamped = Math.max(-1, Math.min(1, s));
      out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    }
    // Preserve the tail samples we didn't consume so the next callback
    // continues from the right offset.
    const consumed = Math.floor(outLen * this.ratio);
    this.carry = Array.from(buf.slice(consumed));

    this.port.postMessage({ pcm: out.buffer }, [out.buffer]);
    return true;
  }
}
registerProcessor('pcm-downsampler', PcmDownsampler);
`;

export interface PcmPipeline {
  /** Async iterator the Transcribe SDK consumes as its AudioStream. */
  stream: AsyncIterable<{ AudioEvent: { AudioChunk: Uint8Array } }>;
  /** Pause — releases the MediaStreamTrack entirely. The pipeline ends; the
   *  caller must call `start()` again (which goes through getUserMedia + a
   *  fresh Transcribe session) to resume. */
  stop: () => void;
}

/** Opens the mic, builds the audio graph, and returns an iterator yielding
 *  Transcribe-ready audio events. Caller holds the stop handle to tear the
 *  pipeline down. Throws if getUserMedia is refused or the worklet fails to
 *  register. */
export async function openPcmPipeline(): Promise<PcmPipeline> {
  const mediaStream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    video: false,
  });

  const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const audioCtx = new AudioCtx();

  const workletBlob = new Blob([WORKLET_SOURCE], { type: 'application/javascript' });
  const workletUrl = URL.createObjectURL(workletBlob);
  try {
    await audioCtx.audioWorklet.addModule(workletUrl);
  } finally {
    URL.revokeObjectURL(workletUrl);
  }

  const source = audioCtx.createMediaStreamSource(mediaStream);
  const node = new AudioWorkletNode(audioCtx, 'pcm-downsampler');
  source.connect(node);
  // No destination connection — we don't want the mic playing back through
  // speakers. The processor still runs because we hold the node reference.

  // Bridge the port messages into an async iterator with backpressure via a
  // bounded buffer. Transcribe accepts ~250 ms of audio per event; our
  // worklet posts whatever each process() call produced (~128 samples at
  // the context rate, i.e. ~42 samples at 16 kHz, so we aggregate upstream).
  let closed = false;
  const buffered: Uint8Array[] = [];
  let notify: (() => void) | null = null;

  node.port.onmessage = (ev: MessageEvent<{ pcm: ArrayBuffer }>) => {
    if (closed) return;
    buffered.push(new Uint8Array(ev.data.pcm));
    notify?.();
  };

  async function* iterate(): AsyncGenerator<{ AudioEvent: { AudioChunk: Uint8Array } }> {
    // Transcribe recommends 50–250 ms audio chunks. Our worklet emits a
    // chunk every quantum (~2.7 ms at 48 kHz source, ~84 bytes at 16 kHz
    // out), so without aggregation we fire ~375 events/sec at Transcribe.
    // Tiny events get batched server-side (added latency) and the per-event
    // framing overhead starves the WebSocket, both of which lag live
    // partials multiple seconds behind real time.
    //
    // Target ~100 ms per yield: 16 kHz × 2 bytes = 32 bytes/ms × 100 ms =
    // 3200 bytes. We wait up to BATCH_WINDOW_MS to fill a batch, and yield
    // whatever we have at the deadline. In steady state this is 10
    // events/sec, which is in Transcribe's happy range.
    const BATCH_BYTES = 3200;
    const BATCH_WINDOW_MS = 100;
    try {
      while (!closed) {
        const chunks: Uint8Array[] = [];
        let total = 0;
        const deadline = performance.now() + BATCH_WINDOW_MS;
        while (!closed && total < BATCH_BYTES) {
          if (buffered.length > 0) {
            const c = buffered.shift()!;
            chunks.push(c);
            total += c.byteLength;
            continue;
          }
          const remaining = deadline - performance.now();
          if (remaining <= 0) break;
          await new Promise<void>((resolve) => {
            const timer = window.setTimeout(() => {
              notify = null;
              resolve();
            }, remaining);
            notify = () => {
              window.clearTimeout(timer);
              notify = null;
              resolve();
            };
          });
        }
        if (closed) break;
        if (total === 0) continue;
        const merged = new Uint8Array(total);
        let off = 0;
        for (const c of chunks) {
          merged.set(c, off);
          off += c.byteLength;
        }
        yield { AudioEvent: { AudioChunk: merged } };
      }
    } finally {
      // Make sure the pipeline is torn down even if the consumer breaks
      // the iteration early (e.g. Transcribe closes the stream).
      stop();
    }
  }

  function stop() {
    if (closed) return;
    closed = true;
    try { node.disconnect(); } catch { /* already disconnected */ }
    try { source.disconnect(); } catch { /* already disconnected */ }
    try { void audioCtx.close(); } catch { /* already closed */ }
    for (const track of mediaStream.getTracks()) {
      try { track.stop(); } catch { /* already stopped */ }
    }
    notify?.();
  }

  return { stream: iterate(), stop };
}
